import { message } from 'antd';

import ServerErrorToText from 'texts/ServerErrorToText';
import {
  API_BASE_URL,
  MAX_RECONNECTION_TRIES,
  RECONNECTION_INTERVAL,
  ACTION_SET_SYNC_STORE,
} from 'globals/constants';
import { sleep } from './utils';

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} res       A resonse from a network request
 *
 * @return {object|undefined} Returns either the res, or throws an error
 */
function checkStatus(res) {
  if (res.status >= 200 && res.status < 300) {
    return res;
  }
  const error = new Error(res.statusText);
  error.res = res;
  throw error;
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} res      A resonse from a network request
 *
 * @return {object}          The parsed JSON from the request, or null
 */
function parseJSON(res) {
  if (res.status === 204 || res.status === 205) {
    return null;
  }
  return res.json();
}

/**
 * Send request to the API endpoint, returning a promise
 *
 * @param {string} subUrl  The partial URL (will be prefixed with API_BASE_URL)
 * @param {object} options The options to pass to fetch()
 *
 * @returns {object}       Promise (as returned by fetch())
 */
export const requestAPI = (subURL, options) =>
  fetch(`${API_BASE_URL}${subURL}`, options)
    .then(checkStatus)
    .then(parseJSON);

/**
 * Create a cancellable version of requestAPI() and the corresponding cancel()
 *  function to cancel the request. Calling cancellableRequestAPI() will cancel
 *  all existing fetch requests made by cancellableRequestAPI().
 *
 * @returns {*} [(APIURL, options) => Promise, () => void]
 */
export const createCancellableRequestAPI = () => {
  let currAbortController = new AbortController();
  return [
    (APIURL, options) => {
      currAbortController.abort(); // Abort reentrant fetch
      currAbortController = new AbortController();
      return requestAPI(APIURL, {
        ...options,
        signal: currAbortController.signal,
      });
    },
    () => currAbortController.abort(),
  ];
};

/**
 * Create a fetch callback that handles syncStore from server
 *
 * @param {func} dispatch Dispatch function used to add syncStore to redux store
 *
 * @returns {func}        (resObject => resObject) Fetch callback in then()
 */

export const createSyncStoreHandler = dispatch => resObject => {
  if (resObject.syncStore !== undefined) {
    dispatch({
      type: ACTION_SET_SYNC_STORE,
      syncStore: resObject.syncStore,
    });
  }
  return resObject;
};

export const fetchTableDataWithRetry = async (
  cancellableRequestAPI,
  apiUrl,
  fetchOptions,
  data,
  setData,
  dispatch,
  extraFieldsToReceive, // Optional
) => {
  try {
    if (data.isFetching) {
      for (let i = MAX_RECONNECTION_TRIES; i >= 0; i -= 1) {
        try {
          const page = data.pagination.current;
          const searchParam =
            data.search.length > 0
              ? `&q=${encodeURIComponent(data.search)}`
              : '';
          let sortParam = '';
          if (data.sorter.order === 'ascend') {
            sortParam = `&s=${data.sorter.field}`;
          } else if (data.sorter.order === 'descend') {
            sortParam = `&s=-${data.sorter.field}`;
          }
          const filtersParam = Object.keys(data.filters)
            .map(filterField =>
              data.filters[filterField]
                .map(filterValue => `&${filterField}=${filterValue}`)
                .join(''),
            )
            .join('');
          const countParam = data.toIncludeCount ? '' : '&x=1';

          // eslint-disable-next-line no-await-in-loop
          const resObject = await cancellableRequestAPI(
            `${apiUrl}?p=${page}${searchParam}${sortParam}${filtersParam}${countParam}`,
            fetchOptions,
          );
          const extraData = {};
          if (Array.isArray(extraFieldsToReceive)) {
            extraFieldsToReceive.forEach(extraField => {
              extraData[extraField] = resObject[extraField];
            });
          }
          setData({
            ...data,
            ...extraData,
            isFetching: false,
            error: false,
            dataSource: resObject.v.map((record, index) => ({
              number: index + 1,
              ...record,
            })),
            pagination:
              resObject.c !== undefined
                ? { ...data.pagination, total: resObject.c }
                : data.pagination,
          });
          createSyncStoreHandler(dispatch)(resObject);
          break;
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }
          if (error.res) {
            setData({
              ...data,
              isFetching: false,
              error: true,
            });
            try {
              // eslint-disable-next-line no-await-in-loop
              const errorObject = await error.res.json();
              message.error(
                ServerErrorToText[errorObject.error] ||
                  ServerErrorToText.internetConnectionError,
              );
              createSyncStoreHandler(dispatch)(errorObject);
            } catch (jsonError) {
              message.error(ServerErrorToText.unknownError);
            }
            break;
          } else if (i === 0) {
            setData({
              ...data,
              isFetching: false,
              error: true,
            });
            message.error(ServerErrorToText.internetConnectionError, 5);
            break;
          } else {
            // eslint-disable-next-line no-await-in-loop
            await sleep(RECONNECTION_INTERVAL);
          }
        }
      }
    }
  } catch (loopError) {
    // DEBUG
    console.error('fetchTableDataWithRetry loopError', loopError);
  }
};

/**
 * Asynchronously fetch a single record to be displayed in a table. Include
 *  status checking, setting data, error handling, abort and auto-retry.
 *
 * @param {object} data                Data state (must have isFetching, error, values)
 * @param {func} cancellableRequestAPI Cancellable requestAPI() generated by createCancellableRequestAPI()
 * @param {string} apiUrl              Partial URL of API endpoint (GET)
 * @param {object} fetchOptions        The options to pass to fetch()
 * @param {func} setData               Data state setter function
 */
export const fetchCustomDataWithRetry = async (
  cancellableRequestAPI,
  apiUrl,
  fetchOptions,
  data,
  setData,
  dispatch,
  resObjectToNewData,
) => {
  try {
    if (data.isFetching) {
      for (let i = MAX_RECONNECTION_TRIES; i >= 0; i -= 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const resObject = await cancellableRequestAPI(apiUrl, fetchOptions);
          setData({
            ...data,
            isFetching: false,
            error: false,
            ...(resObjectToNewData ? resObjectToNewData(resObject) : {}),
          });
          createSyncStoreHandler(dispatch)(resObject);
          return;
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }
          if (error.res) {
            setData({
              ...data,
              isFetching: false,
              error: true,
            });
            try {
              // eslint-disable-next-line no-await-in-loop
              const errorObject = await error.res.json();
              message.error(
                ServerErrorToText[errorObject.error] ||
                  ServerErrorToText.internetConnectionError,
              );
              createSyncStoreHandler(dispatch)(errorObject);
            } catch (jsonError) {
              message.error(ServerErrorToText.unknownError);
            }
            break;
          } else if (i === 0) {
            setData({
              ...data,
              isFetching: false,
              error: true,
            });
            message.error(ServerErrorToText.internetConnectionError, 5);
            break;
          } else {
            // eslint-disable-next-line no-await-in-loop
            await sleep(RECONNECTION_INTERVAL);
          }
        }
      }
    }
  } catch (loopError) {
    // DEBUG
    console.error('fetchCustomDataWithRetry loopError', loopError);
  }
};

/**
 * Asynchronously fetch a single record to be displayed in a table. Include
 *  status checking, setting data, error handling, abort and auto-retry.
 *
 * @param {object} data                Data state (must have isFetching, error, values)
 * @param {func} cancellableRequestAPI Cancellable requestAPI() generated by createCancellableRequestAPI()
 * @param {string} apiUrl              Partial URL of API endpoint (GET)
 * @param {object} fetchOptions        The options to pass to fetch()
 * @param {func} setData               Data state setter function
 */
export const fetchCustomData = async (
  cancellableRequestAPI,
  apiUrl,
  fetchOptions,
  onSuccess, // resObject => resObject
  onError, // errorObject => errorObject
  dispatch,
) => {
  try {
    try {
      // eslint-disable-next-line no-await-in-loop
      const resObject = await cancellableRequestAPI(apiUrl, fetchOptions);
      createSyncStoreHandler(dispatch)(onSuccess(resObject));
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      if (error.res) {
        // eslint-disable-next-line no-await-in-loop
        try {
          const errorObject = await error.res.json();
          message.error(
            ServerErrorToText[errorObject.error] ||
              ServerErrorToText.internetConnectionError,
          );
          createSyncStoreHandler(dispatch)(onError(errorObject));
        } catch (jsonError) {
          message.error(ServerErrorToText.unknownError);
          onError({});
        }
      } else {
        message.error(ServerErrorToText.internetConnectionError, 5);
        onError({});
      }
    }
  } catch (outerError) {
    // DEBUG
    console.error('fetchCustomData outerError', outerError);
  }
};

export const generateDownloadUrlParam = data => {
  let urlParam = '';
  if (data.search.length > 0) {
    urlParam += `&q=${encodeURIComponent(data.search)}`;
  }
  if (data.sorter.order === 'ascend') {
    urlParam += `&s=${data.sorter.field}`;
  } else if (data.sorter.order === 'descend') {
    urlParam += `&s=-${data.sorter.field}`;
  }
  urlParam += Object.keys(data.filters)
    .map(filterField =>
      data.filters[filterField]
        .map(filterValue => `&${filterField}=${filterValue}`)
        .join(''),
    )
    .join('');
  return urlParam.length > 0 ? `?${urlParam.substr(1)}` : '';
};
